// `reports/generate-user-usage-report-form/*` — the filter bar above the usage table.
import { createElement, useEffect, useState, type ReactElement } from 'react'
import type { UsageReportRequest, UserDepartment } from '@woo-ext/api'
import { getUserDepartments, withoutNoneOfTheAbove } from '@woo-ext/api'
import { WooRadio } from '../common/WooRadio.tsx'
import { DatePickerInput } from './DatePickerInput.tsx'

export interface GenerateUserUsageReportFormProps {
  onSubmit: (request: UsageReportRequest) => void
  /** Injected by tests; the screen fetches its own otherwise. */
  loadDepartments?: () => Promise<UserDepartment[]>
}

/**
 * ⚠️ "To" DEFAULTS TO TODAY, AND THAT IS WHY THIS ROUTE'S REFERENCE HAS A CLOCK IN IT.
 * `$onInit` does `this.toDate = this.today`, so the field is pre-filled with the day the page
 * was opened and the screen changes daily on its own. The comparison pins its clock to the
 * reference's capture date for exactly this (`ScoreOptions.capturedAt`); do not "fix" the
 * drift by making this field empty, which would be a redesign and would also break the
 * `required` validation the submit depends on.
 */
export function GenerateUserUsageReportForm({
  onSubmit,
  loadDepartments = getUserDepartments,
}: GenerateUserUsageReportFormProps): ReactElement {
  const [today] = useState(() => new Date())
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined)
  const [toDate, setToDate] = useState<Date | undefined>(today)
  const [departments, setDepartments] = useState<UserDepartment[]>([])
  const [departmentName, setDepartmentName] = useState('')
  // A STRING, not a boolean, because the radio value is one — upstream keeps `'true'`/`'false'`
  // on the model and converts only when building the request.
  const [activeUsersOnly, setActiveUsersOnly] = useState<'true' | 'false'>('true')
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    let live = true
    void loadDepartments()
      .then((all) => {
        // ⚠️ The "None of the above" row is filtered out by ID. It is the only row this
        // tenant returns, so without the filter the chooser offers it as a department.
        if (live) setDepartments(withoutNoneOfTheAbove(all))
      })
      .catch(() => {
        // Upstream has no handler here either: the chooser degrades to "Any department".
      })
    return () => {
      live = false
    }
  }, [loadDepartments])

  const invalid = toDate === undefined

  const submit = (): void => {
    setSubmitted(true)
    if (invalid) return
    onSubmit({
      // ⚠️ AN ABSENT `fromTimestamp` IS AN ABSENT KEY, not a null or a 0. Upstream `delete`s
      // it from the request object when the field is cleared, and the CSV filename branches
      // on exactly that ("until <to>" versus "<from> to <to>").
      ...(fromDate === undefined ? {} : { fromTimestamp: fromDate.getTime() }),
      toTimestamp: toDate.getTime(),
      ...(departmentName === '' ? {} : { department: departmentName }),
      activeUsersOnly: activeUsersOnly === 'true',
    })
  }

  return createElement(
    'generate-user-usage-report-form',
    null,
    // `ng-form`, not `form`: upstream nests this inside the page and never submits it
    // natively, so there is no <form> element and no submit event. A real <form> here would
    // reload the iframe on Enter.
    <div className="generate-user-usage-report-form">
      <div className="date-group flx-form-group">
        <div className="from-date-group flx-form-group">
          <label htmlFor="from-date">From</label>
          <DatePickerInput
            id="from-date"
            name="fromDate"
            value={fromDate}
            onChange={setFromDate}
            // `max-date="ctrl.toDate || ctrl.today"` — the To date when one is set, else today.
            maxDate={toDate ?? today}
          />
        </div>
        <div className="to-date-group flx-form-group">
          <label htmlFor="to-date">To</label>
          <DatePickerInput
            id="to-date"
            name="toDate"
            value={toDate}
            onChange={setToDate}
            minDate={fromDate}
            maxDate={today}
            required
          />
          {invalid && submitted ? (
            <label className="control-label has-error">Please select &quot;To&quot; date</label>
          ) : null}
        </div>
      </div>

      <div className="department-group flx-form-group">
        <label htmlFor="department">Department</label>
        <div className="department-select select">
          <select
            id="department"
            className="department form-control"
            name="department"
            value={departmentName}
            onChange={(event) => {
              setDepartmentName(event.target.value)
            }}
          >
            {/* The empty option is in the template, before the `ng-options` list. */}
            <option value="">Any department</option>
            {departments.map((department) => (
              <option key={department.departmentId} value={department.departmentName}>
                {department.departmentName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="active-users-only-group flx-form-group">
        <label htmlFor="active-users-only">User status</label>
        <ul className="active-users-only-options">
          <li className="active-users-only-option">
            <WooRadio
              name="activeUsersOnly"
              value="true"
              checked={activeUsersOnly === 'true'}
              onChange={() => {
                setActiveUsersOnly('true')
              }}
            >
              Active users
            </WooRadio>
          </li>
          <li className="active-users-only-option">
            <WooRadio
              name="activeUsersOnly"
              value="false"
              checked={activeUsersOnly === 'false'}
              onChange={() => {
                setActiveUsersOnly('false')
              }}
            >
              All users
            </WooRadio>
          </li>
        </ul>
      </div>

      <div className="submit-btn-group flx-form-group">
        <button className="submit-btn btn btn-primary" type="button" onClick={submit}>
          Generate report
        </button>
      </div>
    </div>,
  )
}
